// Canvas renderer that reproduces Captions.tsx for the video export. The DOM
// caption layer (flex-wrap of <span>s, current word highlighted) is re-drawn with
// fillText so it can be composited into the exported frame. All spacing is derived
// from the (export-scaled) font size, mirroring the rem/px utilities Captions uses
// at the on-screen base font of 16px (gap-x-2=8, py/px pad=6, bottom-4=16, px-6=24).
import type { VisibleWord, Mode } from "../types";
import { redactWord, isRedacted } from "../profanity";
import type { CaptionMask, BleepMode } from "../../config/censorship";
import { DEFAULT_CAPTION_MASK, DEFAULT_BLEEP_MODE } from "../../config/censorship";

const HIGHLIGHT: Record<Mode, string> = { roast: "#E62D23", toast: "#5382FF" };
// Solid redaction bar for captionMask="box" on non-highlighted profane words. Dark grey
// (not black) so the bar stays visible over black backdrops / letterbox bars.
// MUST stay in sync with REDACT_INK in Captions.tsx (hand-synced renderers).
const REDACT_INK = "#3a3a3a";

export interface CaptionFont {
  /** Font pixel size at EXPORT resolution (on-screen 16px × export/stage scale). */
  sizePx: number;
  family: string;
}

export interface DrawCaptionsOpts {
  mode: Mode;
  highlightActive: boolean;
  dims: { w: number; h: number };
  font: CaptionFont;
  /** Caption redaction style; default "vowels" (== today). */
  captionMask?: CaptionMask;
  /** Roast's bleep mode; default "tone". Gates whether soundalike stand-ins are redactable. */
  bleepMode?: BleepMode;
}

interface LaidWord { text: string; current: boolean; boxW: number; textW: number; redacted: boolean }

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  // Prefer the native path; fall back to a plain rect when roundRect is absent.
  const c = ctx as CanvasRenderingContext2D & { roundRect?: (x: number, y: number, w: number, h: number, r: number) => void };
  if (typeof c.roundRect === "function") {
    ctx.beginPath();
    c.roundRect(x, y, w, h, r);
  } else {
    ctx.beginPath();
    ctx.rect(x, y, w, h);
  }
}

export function drawCaptions(
  ctx: CanvasRenderingContext2D,
  words: VisibleWord[],
  { mode, highlightActive, dims, font, captionMask = DEFAULT_CAPTION_MASK, bleepMode = DEFAULT_BLEEP_MODE }: DrawCaptionsOpts,
): void {
  if (!words.length) return;

  const fs = font.sizePx;
  const gap = fs * 0.5;        // gap-x-2 (8px @16)
  const lineGap = fs * 0.25;   // gap-y-1 (4px @16)
  const padX = fs * 0.375;     // span padding (6px @16)
  const padY = fs * 0.375;
  const sidePad = fs * 1.5;    // px-6 (24px @16)
  const bottomMargin = fs * 1.0; // bottom-4 (16px @16)
  const maxWidth = dims.w - 2 * sidePad;
  const boxH = fs * 1.2 + 2 * padY;

  ctx.font = `700 ${fs}px ${font.family}`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";

  // Measure + wrap into centred lines (greedy, matching flex-wrap).
  const laid: LaidWord[] = words.map((w) => {
    const text = redactWord(w.word, captionMask, bleepMode);
    const textW = ctx.measureText(text).width;
    return {
      text,
      current: highlightActive && w.state === "current",
      boxW: textW + 2 * padX,
      textW,
      redacted: captionMask === "box" && isRedacted(w.word, bleepMode),
    };
  });

  const lines: LaidWord[][] = [];
  let line: LaidWord[] = [];
  let lineW = 0;
  for (const lw of laid) {
    const add = (line.length ? gap : 0) + lw.boxW;
    if (line.length && lineW + add > maxWidth) {
      lines.push(line);
      line = [lw];
      lineW = lw.boxW;
    } else {
      line.push(lw);
      lineW += add;
    }
  }
  if (line.length) lines.push(line);

  const blockH = lines.length * boxH + (lines.length - 1) * lineGap;
  let y = dims.h - bottomMargin - blockH; // top of the block

  for (const ln of lines) {
    const lineWidth = ln.reduce((a, w) => a + w.boxW, 0) + gap * (ln.length - 1);
    let x = (dims.w - lineWidth) / 2;
    const cy = y + boxH / 2;
    for (const w of ln) {
      if (w.current) {
        // scale(1.1) about the box centre, mirroring the DOM transform
        const grow = 1.1;
        const gw = w.boxW * grow;
        const gh = boxH * grow;
        const gx = x - (gw - w.boxW) / 2;
        const gy = cy - gh / 2;
        ctx.fillStyle = HIGHLIGHT[mode];
        roundRectPath(ctx, gx, gy, gw, gh, fs * 0.25);
        ctx.fill();
        // Box mask: the colored pill IS the redaction bar → draw no text (mirrors the
        // DOM transparent-text span). Otherwise paint the white face on top.
        if (!w.redacted) {
          ctx.font = `700 ${fs * grow}px ${font.family}`;
          ctx.fillStyle = "#FFFFFF";
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
          ctx.fillText(w.text, x + padX - (w.textW * (grow - 1)) / 2, cy);
          ctx.font = `700 ${fs}px ${font.family}`;
        }
      } else if (w.redacted) {
        // Solid redaction bar for a non-highlighted profane word: word-sized rounded
        // rect, no text/outline. Geometry mirrors the DOM span's padded box (boxW × boxH,
        // r = fs*0.25) so the on-screen and exported bars match.
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
        ctx.fillStyle = REDACT_INK;
        roundRectPath(ctx, x, cy - boxH / 2, w.boxW, boxH, fs * 0.25);
        ctx.fill();
      } else {
        // Thick OUTLINE + a tight, low-blur DOWNWARD shadow → an "extruded" look that
        // stays legible on light backgrounds. The stroke (carrying its own offset
        // shadow) is drawn first, then the crisp white face on top. Round joins avoid
        // spiky corners.
        ctx.lineJoin = "round";
        ctx.miterLimit = 2;
        ctx.lineWidth = fs * 0.35;           // thick outline (visible ≈ fs*0.175 after the fill)
        ctx.strokeStyle = "rgba(0,0,0,0.95)";
        ctx.shadowColor = "rgba(0,0,0,1)";
        ctx.shadowBlur = fs * 0.04;          // barely blurred
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = fs * 0.14;       // pushed down → extruded
        ctx.strokeText(w.text, x + padX, cy);
        ctx.shadowColor = "transparent";     // crisp white face, no shadow
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
        ctx.fillStyle = "#FFFFFF";
        ctx.fillText(w.text, x + padX, cy);
      }
      x += w.boxW + gap;
    }
    y += boxH + lineGap;
  }
}
